/**
 * useTrafficSource — iter328 (Traffic-source coverage).
 *
 * Fires ONE analytics ping per browser session — first-touch preserved.
 * Runs on EVERY page (not just /vehicle/:id) so a visitor landing on
 * `/`, `/marketplace`, `/marine`, `/tiktok`, etc. is attributed.
 *
 * Priority (matches backend `_classify_referrer` + `_normalize_source`):
 *   1. utm_source from URL (any known platform, verbatim if unknown)
 *   2. document.referrer host classification
 *   3. `direct` when neither is present
 *
 * The hook is NOT a replacement for `useShareVisitTracker` on the
 * vehicle-detail page — both can coexist. The vehicle-detail tracker
 * still emits its own per-vehicle ping (with the correct vehicle_id).
 * We de-dup at the backend by (utm_source + path + ip) inside a
 * one-hour window so no visit is counted twice.
 *
 * Storage: sessionStorage flag `cartal:first_touch:v1` prevents
 * re-firing on internal navigation. First-touch payload is also
 * cached in sessionStorage for future first-touch attribution.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "./api";

const FIRST_TOUCH_FLAG = "cartal:first_touch:v1";
const FIRST_TOUCH_DATA = "cartal:first_touch:data:v1";

export function useTrafficSource() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      // Already fired for this browser session — respect first-touch.
      if (window.sessionStorage.getItem(FIRST_TOUCH_FLAG)) return;

      // Vehicle-detail pages have their own per-listing tracker
      // (`useShareVisitTracker`) that fires with the correct
      // vehicle_id + UTM. To avoid double-counting, we defer to it —
      // once it fires, IT will set our first-touch flag so any later
      // navigation stays quiet too.
      if (/^\/vehicle\//.test(location.pathname)) {
        return;
      }

      const params = new URLSearchParams(location.search);
      const utm_source   = params.get("utm_source")   || null;
      const utm_medium   = params.get("utm_medium")   || null;
      const utm_campaign = params.get("utm_campaign") || null;
      const utm_content  = params.get("utm_content")  || null;
      const utm_term     = params.get("utm_term")     || null;
      const referrer     = (typeof document !== "undefined" && document.referrer) || null;

      // Only skip if there is neither a UTM nor an external referrer
      // AND we're inside our own site (avoid marking every internal
      // page-load as "direct"). If referrer is same-origin, skip.
      let sameOrigin = false;
      try {
        if (referrer) {
          const u = new URL(referrer);
          sameOrigin = u.origin === window.location.origin;
        }
      } catch (_e) {
        sameOrigin = false;
      }
      // We still want to record `direct` (no UTM + no referrer) so the
      // admin sees direct-traffic volume — but ONLY once per session.

      // Mark BEFORE the async call so a flaky network doesn't cause
      // a re-fire on the immediate next route change.
      window.sessionStorage.setItem(FIRST_TOUCH_FLAG, "1");

      // If referrer is same-origin AND no UTM, don't attribute at all.
      if (sameOrigin && !utm_source) {
        return;
      }

      const payload = {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
        path: location.pathname,
        referrer,
        vehicle_id: null,
      };

      // Cache first-touch for later attribution (e.g., signup, listing
      // publish) — additive, does not affect existing flows.
      try {
        window.sessionStorage.setItem(FIRST_TOUCH_DATA, JSON.stringify(payload));
      } catch (_e) { /* private mode */ }

      apiClient.post("/analytics/share-visits", payload).catch(() => {
        // Silent — analytics must never break UX. If the request
        // failed we still keep the first-touch flag so we don't
        // hammer the endpoint on every route change.
      });
    } catch (_err) {
      /* ignore — analytics is best-effort */
    }
    // Intentionally depend on pathname/search: the hook may be
    // mounted before the first URL is available; we re-check on the
    // first real route. The session flag guarantees a single ping.
  }, [location.pathname, location.search]);
}

export default useTrafficSource;

// Convenience component so App.js can mount it declaratively next to
// <Ga4PageViews />: <TrafficSourceTracker /> — no props, no children.
export function TrafficSourceTracker() {
  useTrafficSource();
  return null;
}

// First-touch snapshot accessor. Additive — reads sessionStorage cache
// written by the tracker above; returns null when nothing is stored
// (e.g., a returning user in the same tab after clearing storage).
export function getFirstTouch() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(FIRST_TOUCH_DATA);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}
